/**
 * Generar el mes.
 *
 * La previsualización y la generación llaman a la **misma** función del
 * servidor, así que lo que se ve acá es exactamente lo que se va a escribir.
 * Y volver a generar es seguro: la idempotencia es un índice único, no una
 * comprobación de la pantalla.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { periodLabel, startOfMonth, today } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Empty, Money, Note, PageHead, Stat, Table,
} from '@/components/ui';

interface Payer {
  party_id: string; name: string; share_basis: number | null; share_kind: string;
  due: string; can_invoice: boolean;
}

interface Line {
  subscription_id: string; unit_code: string; unit_name: string;
  service_name: string; total: string; subtotal: string; discount: string; tax: string;
  allocation_mode: string | null; allocation_confirmed: boolean;
  rotation_index: number | null; payers: Payer[];
  existing_charge_id: string | null; validation: string | null; skip_reason: string | null;
}

interface Preview {
  period_start: string;
  lines: Line[];
  summary: {
    ready: number; skipped: number; blocked: number; already_generated: number;
    unconfirmed_allocation: number; total: string;
  };
}

export function GeneratePage() {
  const [period, setPeriod] = useState(() => startOfMonth(today()).slice(0, 7));
  const query = useApi<Preview>(`/admin/preview?period=${period}`);
  const [result, setResult] = useState<{ created: number; skipped: number; total: string } | null>(null);

  const generate = useApiMutation(
    () => api.post<{ created: number; skipped: number; total: string }>('/admin/generate', { period }),
    ['/admin/preview', '/admin/charges', '/admin/dashboard', '/admin/receivables'],
    { onSuccess: setResult },
  );

  return (
    <>
      <PageHead
        title="Generar el mes"
        subtitle="Se calcula todo antes de escribir nada. Lo que ves acá es lo que se va a emitir."
        actions={
          <input
            className="input"
            type="month"
            value={period}
            onChange={(e) => { setPeriod(e.target.value); setResult(null); }}
            style={{ width: 170 }}
          />
        }
      />

      {result && (
        <Note tone="ok">
          Se emitieron <strong>{result.created}</strong> cobros por <Money value={result.total} />.
          {' '}<Link to={`/admin/cobros?period=${period}`}>Verlos</Link>
        </Note>
      )}

      <Async query={query}>
        {(data) => (
          <>
            <div className="grid grid--4 mt-2">
              <Stat accent label="Listos para emitir" value={data.summary.ready}
                meta={<Money value={data.summary.total} />} />
              <Stat label="Ya generados" value={data.summary.already_generated}
                meta="No se duplican" />
              <Stat label="Omitidos" value={data.summary.skipped} meta="Cada uno con su motivo" />
              <Stat label="Con problema" value={data.summary.blocked}
                meta={data.summary.blocked > 0 ? 'Hay que arreglarlos' : 'Ninguno'} />
            </div>

            {data.summary.blocked > 0 && (
              <div className="mt-3">
                <Note tone="danger">
                  Hay paquetes con un problema en su reparto. Esos no se van a emitir hasta arreglarlos.
                </Note>
              </div>
            )}

            {data.summary.unconfirmed_allocation > 0 && (
              <div className="mt-2">
                <Note tone="warn">
                  <strong>{data.summary.unconfirmed_allocation}</strong> de los cobros listos usan un
                  reparto que nadie confirmó todavía: se está asumiendo partes iguales.
                  {' '}<Link to="/admin/contratos">Revisarlos</Link>
                </Note>
              </div>
            )}

            <div className="mt-3">
              <Card
                title={`Previsualización · ${periodLabel(data.period_start)}`}
                flush
                actions={
                  <Button
                    variant="primary"
                    disabled={data.summary.ready === 0 || generate.isPending}
                    onClick={() => generate.mutate()}
                  >
                    {generate.isPending
                      ? 'Generando…'
                      : `Emitir ${data.summary.ready} cobro${data.summary.ready === 1 ? '' : 's'}`}
                  </Button>
                }
              >
                {data.lines.length === 0 ? (
                  <Empty icon="lista" title="No hay paquetes que evaluar" />
                ) : (
                  <Table head={
                    <tr>
                      <th>Consultorio</th><th>Servicio</th><th>Reparto</th>
                      <th className="right">Total</th><th>Estado</th>
                    </tr>
                  }>
                    {data.lines.map((line) => (
                      <LineRow key={line.subscription_id} line={line} />
                    ))}
                  </Table>
                )}
              </Card>
            </div>
          </>
        )}
      </Async>
    </>
  );
}

function LineRow({ line }: { line: Line }) {
  const [open, setOpen] = useState(false);

  const status = line.skip_reason
    ? <Badge tone="neutral" title={line.skip_reason}>Omitido</Badge>
    : line.validation
      ? <Badge tone="danger" title={line.validation}>Con problema</Badge>
      : line.existing_charge_id
        ? <Badge tone="info">Ya generado</Badge>
        : <Badge tone="ok">Listo</Badge>;

  const dim = Boolean(line.skip_reason);

  return (
    <>
      <tr style={dim ? { opacity: 0.6 } : undefined}>
        <td className="strong">
          {line.unit_code}
          <span className="tbl__sub">{line.unit_name}</span>
        </td>
        <td>{line.service_name || '—'}</td>
        <td>
          {line.payers.length > 0 ? (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen((v) => !v)}>
              {line.payers.length} pagador{line.payers.length === 1 ? '' : 'es'}
              {line.rotation_index != null && ` · turno ${line.rotation_index + 1}`}
              {!line.allocation_confirmed && ' ·'}
              {!line.allocation_confirmed && <span style={{ color: 'var(--warn)' }}> sin confirmar</span>}
            </button>
          ) : <span className="muted small">—</span>}
        </td>
        <td className="right"><Money value={line.total} dim={dim} /></td>
        <td>{status}</td>
      </tr>

      {/* El motivo de omisión se muestra siempre: nada se descarta en silencio. */}
      {(line.skip_reason || line.validation) && (
        <tr>
          <td colSpan={5} className="small" style={{ paddingTop: 0, color: line.validation ? 'var(--danger)' : 'var(--muted)' }}>
            {line.validation ?? line.skip_reason}
          </td>
        </tr>
      )}

      {open && line.payers.map((p) => (
        <tr key={p.party_id} style={{ background: 'var(--surface-2)' }}>
          <td />
          <td colSpan={2} className="small">
            {p.name}
            {!p.can_invoice && <Badge tone="warn" plain> sin cédula</Badge>}
          </td>
          <td className="right small">
            <Money value={p.due} />
            <span className="tbl__sub">
              {p.share_kind === 'PERCENT' && p.share_basis != null ? `${p.share_basis}%` : ''}
            </span>
          </td>
          <td />
        </tr>
      ))}
    </>
  );
}
